<template>
  <div class="py-7 w-full bg-raster">
    <ConfirmationPopup
      v-if="state.confirmationPopupIsVisible"
      :text="t('deletionwarning')"
      @confirm="deleteWall(state.wall)"
      @close-popup="closeConfirmationPopup()"
    />
    <ConfirmationPopup
      v-if="state.templatePopupIsVisible"
      :text="t('templatenotice')"
      @confirm="useWallAsTemplate(state.template)"
      @close-popup="closeTemplatePopup()"
    />
    <SharingPopup v-if="state.sharingPopupIsVisible" :wall="state.wall" :user-is-owner="userIsOwner" @close-popup="closeSharingPopup" />
    <EditWallPopup v-if="state.editPopupIsVisible" :wall="state.wall" :selected-task-index="0" @close-popup="closeEditPopup" />
    <EditWallPopup v-if="state.wallPopupIsVisible" @close-popup="closePopup" />
    <FileExportPopup v-if="state.exportPopupIsVisible" :wall-id="state.wall._id" @close-popup="closeFileExportPopup" />
    <div class="flex justify-between ml-7 py-2 mb-1">
      <h1 class="text-slate-white md:text-xl text-sm font-bold">{{ translatedFilterName(filter.getters.wallSelection.value) }}</h1>
      <div class="flex mx-7 justify-end items-center h-7">
        <div
          tabindex="0"
          class="bg-slate-bright mr-3 p-1.5 rounded-full cursor-pointer w-7 h-7 flex justify-center items-center"
          @click.prevent="toggleSearchBar()"
          @keydown.enter="toggleSearchBar()"
        >
          <img v-if="!state.searchBarIsVisible" class="w-3.5 p-px invert" :src="Search" :alt="t('aria-open-search')" />
          <img v-else class="w-3.5 p-px invert rotate-45" :src="Plus" :alt="t('aria-close-search')" />
        </div>
        <div v-if="state.searchBarIsVisible" class="mr-4 bg-white rounded-3xl h-7">
          <input v-model="state.searchText" class="text-xs w-37 rounded-3xl h-7 p-1" type="text" :placeholder="t('search')" />
        </div>
        <div class="relative">
          <div v-if="!state.searchBarIsVisible || !common.getters.isMobileView.value" class="flex flex-col h-7 z-51">
            <div
              v-if="selectedFilter.name !== undefined"
              tabindex="0"
              class="flex items-center justify-between cursor-pointer rounded-2xl bg-slate-bright px-3 py-1 w-28"
              @click.prevent="setFilterMenuVisibility()"
              @keydown.enter="setFilterMenuVisibility()"
            >
              <p class="text-slate-white text-sm">{{ t(selectedFilter.name) }}</p>
              <img class="w-2.5 invert -rotate-90 ml-3" :src="Arrow" :alt="t('aria-view-filtering')" />
            </div>
            <div
              v-if="state.filterMenuIsVisible"
              class="absolute text-slate-white text-sm text-left fadein rounded-lg bg-slate-bright p-1 z-151 w-28"
            >
              <p
                v-for="(f, index) in Object.keys(filter.getters.wallFilterCollection.value)"
                :key="index"
                tabindex="0"
                class="text rounded px-5 py-1 my-1 hover:bg-cyan-500 text-left w-full cursor-pointer"
                :class="filterClass(filter.getters.wallFilterCollection.value[f].name)"
                @pointerup="selectFilter(filter.getters.wallFilterCollection.value[f])"
                @keydown.enter="selectFilter(filter.getters.wallFilterCollection.value[f])"
              >
                {{ t(f) }}
              </p>
            </div>
          </div>
        </div>
        <div
          v-if="!state.searchBarIsVisible || !common.getters.isMobileView.value"
          tabindex="0"
          class="flex items-center bg-tw-orange-bright px-1 rounded-2xl cursor-pointer h-7 ml-4"
          @pointerup.prevent="invokeNewWallPopup()"
          @keydown.enter="invokeNewWallPopup()"
        >
          <div class="p-1 rounded-full">
            <img class="w-3 h-3" :src="Plus" :alt="t('aria-new-wall')" />
          </div>
          <p v-if="common.getters.screensizeType.value !== ScreensizeType.phone" class="mx-1.5 text-black text-sm font-extrabold">
            {{ t('newwall') }}
          </p>
        </div>
      </div>
    </div>
    <div class="h-px bg-slate-bright mx-7"></div>
    <div
      v-if="sortedWalls.length > 0"
      id="walls-wrapper"
      ref="wallWrapper"
      :key="state.render"
      class="flex flex-wrap mx-3 py-5 overflow-scroll content-start"
      :class="mobileWallsWrapperStyles"
    >
      <DashboardWall
        v-for="(wall, index) in sortedWalls"
        :key="index"
        class="my-4 mx-3 p-3 rounded-xl lp-bg-gray cursor-pointer wall-component"
        :wall="wall"
        tabindex="0"
        @pointerup="selectWall(wall)"
        @keydown.enter="selectWall(wall)"
      >
        <DashboardWallMenu
          v-if="!isTemplatesView"
          class="z-10 fadein absolute top-2 right-2 p-5 rounded-xl h-fit"
          tabindex="1"
          :wall="wall"
          :active="state.wallMenuIsVisible[index]"
          @copy-wall-link="copyToClipBoard(assembledPermaLink)"
          @copy-wall-pin="copyToClipBoard(wall.details.pin)"
          @duplicate="duplicate(wall, false)"
          @export-file="exportFile()"
          @invoke-deletion-popup="invokeDeletionPopup(wall)"
          @invoke-edit-popup="invokeEditPopup(wall)"
          @toggle-wall-menu="toggleWallMenu(index, wall)"
        ></DashboardWallMenu>
      </DashboardWall>
    </div>
    <div v-else class="full-viewport-height flex justify-center items-center">
      <div v-if="state.loadingDone" class="flex justify-center items-center flex-col">
        <h2 v-if="state.searchText.length === 0" class="text-slate-white text-2xl font-bold m-3">{{ t('nowalls') }}</h2>
        <h2 v-else class="text-slate-white text-2xl font-bold">{{ t('nowallssearch') }}</h2>
        <div
          v-if="state.searchText.length === 0"
          class="bg-blue-middle rounded-2xl p-3 mt-4 cursor-pointer"
          @pointerup.stop="reload()"
          @keydown.enter="reload()"
        >
          <p class="text-white text-sm">{{ t('reload') }}</p>
        </div>
      </div>
      <Loader v-else @set-loading-done="setLoadingDone()" />
    </div>
  </div>
</template>

<script setup lang="ts">
  import { reactive, ref, onUnmounted, onBeforeMount, computed, PropType, nextTick } from 'vue'
  import { useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'

  import { FilterType, WallSelectionType, ScreensizeType, PermissionLevel } from '@/constants'
  import { Wall, DialogMessageType, Task, Board, Note, BoardEntry, Subject } from '@/common/classes'
  import { AttributeFilter, StoreResponse } from '@/common/interfaces'

  import { useNoteStore } from '@/composables/noteStore'
  import useWallStore from '@/composables/wallStore'
  import { useUserStore } from '@/composables/userStore'

  import useFilterStore from '@/composables/filter'
  import useCommonStore from '@/composables/common'
  import useDialogStore from '@/composables/dialog'

  import ConfirmationPopup from '../popups/ConfirmationPopup.vue'
  import SharingPopup from '../popups/SharingPopup.vue'
  import EditWallPopup from '../popups/EditWallPopup.vue'
  import FileExportPopup from '../popups/FileExportPopup.vue'
  import Loader from '@/components/common/Loader.vue'
  import DashboardWall from '@/components/dashboard/DashboardWall.vue'
  import DashboardWallMenu from '@/components/dashboard/DashboardWallMenu.vue'

  import Search from '@/assets/icons/fontawesome/magnifying-glass-solid.svg'
  import Arrow from '@/assets/icons/fontawesome/angle-left-solid.svg'
  import Plus from '@/assets/icons/fontawesome/plus-solid.svg'

  const messages = {
    no: {
      newwall: 'Ny tavle',
      time: 'laget',
      updated: 'oppdatert',
      created: 'Laget',
      edited: 'Redigert',
      nowalls: 'Du har ikke laget eller deltatt på en tavle enda.',
      accessdenied: 'Du har ikke adgang til denne tavlen, spør eieren om adgang',
      share: 'Del',
      copywallpin: 'Kopier PIN til tavle',
      copywalllink: 'Kopier lenke til tavla',
      includingsubject: ': emne',
      sharedby: 'Delt av',
      in: 'i',
    },
    en: {
      newwall: 'New wall',
      time: 'time',
      updated: 'update',
      created: 'Created',
      edited: 'Edited',
      nowalls: 'You have not joined or created a wall yet.',
      accessdenied: "You don't have access to this wall, ask the owner to allow access",
      share: 'Share',
      copywallpin: 'Copy wall PIN',
      copywalllink: 'Copy wall link',
      includingsubject: ': subject',
      sharedby: 'Shared by',
      in: 'in',
    },
  }

  const wallWrapper = ref<HTMLElement | null>(null)

  const filter = useFilterStore()
  const common = useCommonStore()
  const userStore = useUserStore()
  const noteStore = useNoteStore()
  const wallStore = useWallStore()
  const dialogStore = useDialogStore()

  const router = useRouter()

  const state = reactive({
    wallPopupIsVisible: false,
    sharingPopupIsVisible: false,
    exportPopupIsVisible: false,
    editPopupIsVisible: false,
    confirmationPopupIsVisible: false,
    templatePopupIsVisible: false,
    searchBarIsVisible: false,
    filterMenuIsVisible: false,
    loadingDone: false,

    wallMenuIsVisible: [] as boolean[],
    wall: {} as Wall,
    template: {} as Wall,
    render: 0,
    searchText: '',
  })

  const props = defineProps({
    isnewWall: { type: Boolean as PropType<boolean>, required: true },
    courseId: { type: String as PropType<string>, required: false, default: '' },
  })

  const { t } = useI18n({ messages })

  onBeforeMount(() => {
    filter.actions.setWallFilterType(FilterType.TIME) // time is the default filter
    window.addEventListener('resize', () => {
      state.render++
    })
    state.wallPopupIsVisible = props.isnewWall
  })

  onUnmounted(() => {
    window.removeEventListener('resize', () => ({}))
  })

  const userIsOwner = computed(() => {
    const user = userStore.getters.user.value
    const wall = wallStore.getters.selectedWall.value
    if (wall && user) {
      return common.actions.hasMinimumPermission(user, PermissionLevel.Organize, wall)
    }
    return false
  })

  const filteredWalls = computed(() => {
    const user = userStore.getters.user.value
    if (user) {
      let walls = [] as Wall[]
      switch (filter.state.wallSelection) {
        case WallSelectionType.ALL:
          walls = [...wallStore.getters.templates.value, ...wallStore.getters.allWalls.value]
          break
        case WallSelectionType.SHARED:
          wallStore.actions.setSharedWalls(user._id) // set shared walls on click
          walls = wallStore.getters.sharedWalls.value
          break
        case WallSelectionType.MINE:
          walls = wallStore.getters.allWalls.value.filter(
            (w: Wall) => w.users.owner.user_id === user._id || common.actions.hasMinimumPermission(user, PermissionLevel.Organize, w),
          )
          break
        case WallSelectionType.TEMPLATES:
          walls = wallStore.getters.templates.value
      }
      const subjectFilter = filter.getters.subjectFilter.value
      if (subjectFilter.name) walls = walls.filter((w) => w.details.subjects.findIndex((s: Subject) => subjectFilter.name === s.name) > -1)
      return walls
    } else return []
  })

  const sortedWalls = computed(() => {
    let walls = filteredWalls.value.filter(
      (w: Wall) =>
        w.details.title.toLowerCase().includes(state.searchText.toLowerCase()) ||
        w.details.description.toLowerCase().includes(state.searchText.toLowerCase()),
    )

    /**
     * sortByFilterValue()
     *
     * this function works in connection to Array.sort()
     * it determines the correct order of walls of an array by returning the integer '1' with the corresponding prefix (negative / positive)
     * , depending on the selected filter; cValue and nValue represent the current and next wall that will be compared within sort()
     *
     * @param desc boolean
     */
    const sortByFilterValue = (desc: boolean) => {
      let result = desc ? -1 : 1
      return (c: Wall, n: Wall) => {
        let cValue, nValue
        if (filter.getters.selectedWallFilterId.value === FilterType.TIME) {
          cValue = c.status.created
          nValue = n.status.created
        } else {
          cValue = c.status.lastContribution
          nValue = n.status.lastContribution
        }
        // NOTE: status.lastOpened is also available... might be considered in the future
        return cValue > nValue ? result : -result
      }
    }

    walls.sort(sortByFilterValue(true))
    return walls
  })

  const mobileWallsWrapperStyles = computed(() => {
    return common.getters.isMobileView.value ? 'justify-center' : ''
  })

  const selectedFilter = computed(() => {
    return filter.getters.wallFilterCollection.value[filter.getters.selectedWallFilterId.value]
  })

  const assembledPermaLink = computed(() => {
    const port = import.meta.env.VITE_SERVER_HOST.includes('localhost') ? `:${import.meta.env.VITE_CLIENT_PORT}` : ''
    return `${import.meta.env.VITE_SERVER_HOST}${port}/login?link=${state.wall.details.permaLink}`
  })

  //Used to control UI artefacts in the left hand dashboard menu -> template section of the dashboard (i.e. copy wall).
const isTemplatesView = computed(() =>
  // pick ONE of these lines depending on how your store exposes it:
  filter.state.wallSelection === WallSelectionType.TEMPLATES
  
  //Alternatively, is it safer to use a getter?
)

  async function toggleWallMenu(index: number, wall: Wall) {
    state.wallMenuIsVisible[index] = !state.wallMenuIsVisible[index]
    if (state.wallMenuIsVisible[index]) {
      const res = await wallStore.actions.getWall(wall._id)
      if (res.success && wallStore.getters.selectedWall.value) {
        state.wall = wallStore.getters.selectedWall.value
      }
      nextTick(() => {
        const el = document.getElementById('firstFocus-' + index)
        el?.focus()
      })
    }
    for (let i = 0; i < state.wallMenuIsVisible.length; i++) if (i !== index) state.wallMenuIsVisible[i] = false // hide all others
  }

  function invokeStartFromTemplate(wall: Wall) {
    state.templatePopupIsVisible = true
    state.template = wall
  }

  function reload() {
    router.go(0)
  }

  function translatedFilterName(name: string | WallSelectionType) {
    let baseName = ''
    switch (name) {
      case WallSelectionType.ALL:
        baseName = t('allwalls')
        break
      case WallSelectionType.SHARED:
        baseName = t('sharedwalls')
        break
      case WallSelectionType.MINE:
        baseName = t('mywalls')
        break
      case WallSelectionType.TRASHED:
        baseName = t('trashedwalls')
        break
      case WallSelectionType.TEMPLATES:
        baseName = t('templates')
        break
      default:
        baseName = t('allwalls')
    }
    const filterName = filter.getters.subjectFilter.value.name
    if (filterName) baseName += ` ${t('includingsubject')}: '${filterName}'`
    return baseName
  }

  function selectWall(wall: Wall) {
    const user = userStore.getters.user.value
    if (user) {
      if (!wallStore.getters.templates.value.find((t) => t._id === wall._id)) {
        const wallSharedToUser = common.actions.hasMinimumPermission(user, PermissionLevel.Join, wall)
        if (wallSharedToUser || !wall.authorisedAccessOnly || user._id === wall.users.owner.user_id) {
          // access possible if the access was not restricted for non owners, if the user is the actual owner or the wall was shared to the user with proper access
          wallStore.actions.getWall(wall._id).then((res: StoreResponse) => {
            if (res.success) router.push({ name: 'Wall', query: { id: wall._id } })
          })
        } else dialogStore.actions.pushMessage('', DialogMessageType.Warning, t('accessdenied'))
      } else invokeStartFromTemplate(wall)
    }
  }

  async function duplicate(originalWall: Wall, template = false) {
    const user = userStore.getters.user.value
    if (user) {
      const wall = new Wall(originalWall)
      if (!template) wall.prepareDuplication(t('copyOf'))
      else wall.prepareDuplication(t('template'))
      wall.assignNewOwner(user)
      try {
        const res = await wallStore.actions.createWall(wall, true)
        if (res.success) {
          let notes = [] as Note[]

          const eres = await noteStore.actions.getNotesForExternalDuplication(wall._id)
          if (eres.success && eres.body && eres.body instanceof Map && res.newWallId) {
            notes = Array.from(eres.body.values())

            for (let note of notes) {
              let newNote = new Note(note)
              let newWall = wallStore.getters.allWalls.value.find((w: Wall) => w._id == res.newWallId)

              if (newWall) {
                let newTask = getOriginalTaskByNote(wall, newNote, res.newWallId)
                if (newTask) {
                  newNote.task_id = newTask._id
                  newNote.wall_id = res.newWallId // assign new note - wall relation
                  for (let [key, entry] of Object.entries(note.entries)) {
                    let convertedEntry = entry as BoardEntry
                    const newBoardKey = getNewBoardId(wall, key, res.newWallId)
                    if (newBoardKey) {
                      convertedEntry.wall_id = newBoardKey
                      newNote.addBoardEntry(newBoardKey, convertedEntry)
                    }
                  }
                }
                await noteStore.actions.addNewNote(newNote, true)
              }
            }
          }
          state.wallMenuIsVisible = [] // force reload on next click
        }
      } catch (e) {
        dialogStore.actions.pushMessage('Error', DialogMessageType.Error, t('wallduplication'))
      }
    }
  }

  function getOriginalTaskByNote(wall: Wall, note: Note, newWallId: string) {
    let task = wall.tasks.find((t: Task) => t._id === note.task_id) // original task
    if (task) return getDuplicatedTaskByName(task.order, newWallId)
  }

  function getNewBoardId(wall: Wall, originalBoardId: string, newWallId: string) {
    let originalBoardName = wall.boards.find((b: Board) => b._id === originalBoardId)?.name
    if (originalBoardName) {
      let newBoard = wallStore.getters.allWalls.value.find((w: Wall) => w._id === newWallId)?.boards.find((b: Board) => b.name === originalBoardName)
      if (newBoard && newBoard._id) return newBoard._id
    }
  }

  function getDuplicatedTaskByName(order: number, id: string) {
    return wallStore.getters.allWalls.value.find((w: Wall) => w._id === id)?.tasks.find((t: Task) => t.order === order)
  }

  function wallItemStyle(height: boolean, container = false, last = false) {
    if (wallWrapper.value) {
      let itemsPerRow
      switch (common.getters.screensizeType.value) {
        case ScreensizeType.tablet:
          itemsPerRow = 2
          break
        case ScreensizeType.phone:
          itemsPerRow = 1
          break
        default:
          itemsPerRow = 3
      }
      const paddingCompensation = !container ? 20 : -5 // compensate horizontal padding for each item in calculation
      const value = (wallWrapper.value.clientWidth - 100) / itemsPerRow - paddingCompensation
      // setting both height and width prevents the relative width to skew the first element
      let heightValue = height ? 'height: ' + value + 'px;' : ''
      let maxHeightValue = 'max-height: ' + 1.5 * value + 'px;'
      const lastSpacing = common.getters.screensizeType.value === ScreensizeType.phone && last ? 'margin-bottom: 7rem;' : ''
      return 'width: ' + value + 'px;' + heightValue + maxHeightValue + lastSpacing
    }
  }

  async function deleteWall(wall: Wall) {
    await wallStore.actions.deleteWall(wall._id)
  }

  async function useWallAsTemplate(wall: Wall) {
    await duplicate(wall, true)
    await wallStore.actions.getTemplateWalls() // update local templates with the new values
  }

  function copyToClipBoard(text: string) {
    common.actions.copyTextToClipboard(text)
    dialogStore.actions.pushMessage('', DialogMessageType.Information, t('copiedtoclipboard'))
  }

  function filterClass(name: string) {
    return name === selectedFilter.value.name ? 'bg-cyan-500' : ''
  }

  function selectFilter(f: AttributeFilter) {

    setFilterMenuVisibility()
    filter.actions.setWallFilterType(f.name)

    //Set boolean for for template selection to enable hiding of wall menu for templates (i.e. "copy wall")
    //isTemplatesView.value = f.name == WallSelectionType.TEMPLATES
    
  }

  function toggleSearchBar() {
    state.searchBarIsVisible = !state.searchBarIsVisible
  }

  function setFilterMenuVisibility() {
    state.filterMenuIsVisible = !state.filterMenuIsVisible
  }

  // POPUP HANDLING:

  async function invokeEditPopup(wall: Wall) {
    const res = await wallStore.actions.getWall(wall._id)
    if (res.success && wallStore.getters.selectedWall.value) {
      state.editPopupIsVisible = true
      state.wall = wallStore.getters.selectedWall.value
      state.wallMenuIsVisible = []
    }
  }

  function invokeDeletionPopup(wall: Wall) {
    wallStore.actions.getWall(wall._id).then((res: StoreResponse) => {
      if (res.success && wallStore.getters.selectedWall.value) {
        state.confirmationPopupIsVisible = true
        state.wall = wallStore.getters.selectedWall.value
        state.wallMenuIsVisible = []
      }
    })
  }

  function closeEditPopup() {
    state.editPopupIsVisible = false
    wallStore.actions.resetSelectedWall()
  }

  function closeConfirmationPopup() {
    state.confirmationPopupIsVisible = false
  }

  function closeTemplatePopup() {
    state.templatePopupIsVisible = false
  }

  function invokeNewWallPopup() {
    state.wallPopupIsVisible = true
  }

  function closePopup() {
    state.wallPopupIsVisible = false
  }

  function closeSharingPopup() {
    state.sharingPopupIsVisible = false
    wallStore.actions.selectWallFromDashboard(undefined)
  }

  function exportFile() {
    state.exportPopupIsVisible = true
  }

  function closeFileExportPopup() {
    state.exportPopupIsVisible = false
  }

  function setLoadingDone() {
    state.loadingDone = true
  }
</script>

<style scoped>
  #walls-wrapper {
    height: 87vh;
  }
</style>
